import { useState, useEffect, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  ItemMargin,
  ItemYield,
  MarginType,
  MarginTypeEnum,
} from "@forgepoint/shared";
import { MarginInputRow } from "@/modules/items/MarginInputRow";
import { useItemDefaultValuesStore } from "@/store/itemDefaultValuesStore";

const ALL_MARGIN_TYPES: MarginType[] = [
  MarginTypeEnum.all,
  MarginTypeEnum.purchased,
  MarginTypeEnum.operations,
  MarginTypeEnum.outsideOperations,
  MarginTypeEnum.material,
  MarginTypeEnum.manufactured,
];

/** Formats a margin type enum value for display. */
function formatMarginType(marginType: MarginType): string {
  if (marginType === "outsideOperations") {
    return "Outside Operations";
  }
  if (marginType === "operations") {
    return "Inside Operations";
  }
  if (marginType === "manufactured") {
    return "Manufactured";
  }
  return marginType.charAt(0).toUpperCase() + marginType.slice(1);
}

interface MarginTypeGroup {
  marginType: MarginType;
  defaultMargin?: ItemMargin;
  overrides: ItemMargin[];
}

interface ItemDefaultValuesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Sheet component for editing organization-level item default values (margins and yield). */
export function ItemDefaultValuesSheet({
  open,
  onOpenChange,
}: ItemDefaultValuesSheetProps) {
  const defaults = useItemDefaultValuesStore((state) => state.defaults);
  const isSaving = useItemDefaultValuesStore((state) => state.isSaving);
  const updateDefaults = useItemDefaultValuesStore(
    (state) => state.updateDefaults,
  );

  const [margins, setMargins] = useState<ItemMargin[]>([]);
  const [yields, setYields] = useState<ItemYield[]>([]);

  useEffect(() => {
    if (open) {
      setMargins(defaults?.margin ?? []);
      setYields(defaults?.yield ?? []);
    }
  }, [open, defaults]);

  const handleSave = async () => {
    const success = await updateDefaults({
      margin: margins,
      yield: yields,
    });

    if (success) {
      toast.success("Item default values saved");
      onOpenChange(false);
    } else {
      toast.error("Failed to save item default values");
    }
  };

  const defaultYield = yields.find((y) => y.minQty === undefined);
  const yieldOverrides = yields.filter((y) => y.minQty !== undefined);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Item Default Values</SheetTitle>
          <SheetDescription>
            Set default margins and yield rates that apply when items are
            created by the agent without explicit values.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <MarginSection margins={margins} onMarginsChange={setMargins} />

          <YieldSection
            defaultYield={defaultYield}
            overrides={yieldOverrides}
            allYields={yields}
            onYieldsChange={setYields}
          />
        </div>

        <SheetFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function MarginSection({
  margins,
  onMarginsChange,
}: {
  margins: ItemMargin[];
  onMarginsChange: (margins: ItemMargin[]) => void;
}) {
  const groups = useMemo((): MarginTypeGroup[] => {
    const groupMap = new Map<MarginType, MarginTypeGroup>();

    for (const margin of margins) {
      if (!groupMap.has(margin.type)) {
        groupMap.set(margin.type, { marginType: margin.type, overrides: [] });
      }
      const group = groupMap.get(margin.type)!;
      if (margin.minQty === undefined) {
        group.defaultMargin = margin;
      } else {
        group.overrides.push(margin);
      }
    }

    return Array.from(groupMap.values());
  }, [margins]);

  const usedTypes = new Set(groups.map((g) => g.marginType));
  const unusedTypes = ALL_MARGIN_TYPES.filter((t) => !usedTypes.has(t));

  const handleAddMarginType = (marginType: MarginType) => {
    const newMargin: ItemMargin = {
      type: marginType,
      marginAmount: { type: "PERCENTAGE", percentage: "0" },
    };
    onMarginsChange([...margins, newMargin]);
  };

  const handleRemoveMarginType = (marginType: MarginType) => {
    onMarginsChange(margins.filter((m) => m.type !== marginType));
  };

  const handleDefaultChange = (
    marginType: MarginType,
    field: "marginType" | "value",
    value: string,
  ) => {
    onMarginsChange(
      margins.map((m) => {
        if (m.type !== marginType || m.minQty !== undefined) {
          return m;
        }
        if (field === "marginType") {
          return {
            ...m,
            marginAmount:
              value === "PERCENTAGE"
                ? { type: "PERCENTAGE" as const, percentage: "0" }
                : { type: "AMOUNT" as const, amount: "0.00" },
          };
        }
        return {
          ...m,
          marginAmount:
            m.marginAmount.type === "PERCENTAGE"
              ? { type: "PERCENTAGE" as const, percentage: value }
              : { type: "AMOUNT" as const, amount: value },
        };
      }),
    );
  };

  const handleAddOverride = (marginType: MarginType) => {
    const existingForType = margins.filter(
      (m) => m.type === marginType && m.minQty !== undefined,
    );
    const maxQty = Math.max(
      0,
      ...existingForType
        .map((m) => m.minQty)
        .filter((q): q is number => q !== undefined),
    );
    const newOverride: ItemMargin = {
      type: marginType,
      minQty: maxQty + 10,
      marginAmount: { type: "PERCENTAGE", percentage: "0" },
    };
    onMarginsChange([...margins, newOverride]);
  };

  const handleOverrideChange = (
    marginType: MarginType,
    overrideIndex: number,
    field: "minQty" | "marginType" | "value",
    value: string | number,
  ) => {
    const overridesForType = margins.filter(
      (m) => m.type === marginType && m.minQty !== undefined,
    );
    const target = overridesForType[overrideIndex];
    if (!target) {
      return;
    }

    onMarginsChange(
      margins.map((m) => {
        if (m !== target) {
          return m;
        }
        if (field === "minQty") {
          const numValue =
            typeof value === "number" ? value : parseInt(value.toString(), 10);
          return { ...m, minQty: isNaN(numValue) ? 1 : numValue };
        }
        if (field === "marginType") {
          return {
            ...m,
            marginAmount:
              value === "PERCENTAGE"
                ? { type: "PERCENTAGE" as const, percentage: "0" }
                : { type: "AMOUNT" as const, amount: "0.00" },
          };
        }
        return {
          ...m,
          marginAmount:
            m.marginAmount.type === "PERCENTAGE"
              ? { type: "PERCENTAGE" as const, percentage: value.toString() }
              : { type: "AMOUNT" as const, amount: value.toString() },
        };
      }),
    );
  };

  const handleRemoveOverride = (
    marginType: MarginType,
    overrideIndex: number,
  ) => {
    const overridesForType = margins.filter(
      (m) => m.type === marginType && m.minQty !== undefined,
    );
    const target = overridesForType[overrideIndex];
    if (!target) {
      return;
    }
    onMarginsChange(margins.filter((m) => m !== target));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-semibold">Default Margins</Label>
          <p className="text-xs text-muted-foreground">
            Applied to new items created by the agent when no margin is
            specified.
          </p>
        </div>
        {unusedTypes.length > 0 && (
          <Select
            value=""
            onValueChange={(v) => handleAddMarginType(v as MarginType)}
          >
            <SelectTrigger className="h-9 w-[180px]">
              <SelectValue placeholder="Add Margin Type" />
            </SelectTrigger>
            <SelectContent>
              {unusedTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  {formatMarginType(t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {groups.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">
          No margins configured. Select a margin type above to get started.
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const overridesForType = group.overrides;
            return (
              <div
                key={group.marginType}
                className="border rounded-lg p-4 space-y-3"
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold">
                    {formatMarginType(group.marginType)}
                  </h3>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveMarginType(group.marginType)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {group.defaultMargin && (
                  <MarginInputRow
                    isDefault
                    marginType={group.defaultMargin.marginAmount.type}
                    value={
                      group.defaultMargin.marginAmount.type === "PERCENTAGE"
                        ? group.defaultMargin.marginAmount.percentage
                        : group.defaultMargin.marginAmount.amount
                    }
                    onMarginTypeChange={(type) =>
                      handleDefaultChange(group.marginType, "marginType", type)
                    }
                    onValueChange={(value) =>
                      handleDefaultChange(group.marginType, "value", value)
                    }
                  />
                )}

                {overridesForType.length > 0 && (
                  <div className="space-y-2 pl-4 border-l-2 border-gray-200">
                    <span className="text-xs font-medium text-gray-600">
                      Quantity Overrides
                    </span>
                    {overridesForType.map((override, index) => (
                      <MarginInputRow
                        key={index}
                        minQty={override.minQty}
                        marginType={override.marginAmount.type}
                        value={
                          override.marginAmount.type === "PERCENTAGE"
                            ? override.marginAmount.percentage
                            : override.marginAmount.amount
                        }
                        onMinQtyChange={(qty) =>
                          handleOverrideChange(
                            group.marginType,
                            index,
                            "minQty",
                            qty,
                          )
                        }
                        onMarginTypeChange={(type) =>
                          handleOverrideChange(
                            group.marginType,
                            index,
                            "marginType",
                            type,
                          )
                        }
                        onValueChange={(value) =>
                          handleOverrideChange(
                            group.marginType,
                            index,
                            "value",
                            value,
                          )
                        }
                        onRemove={() =>
                          handleRemoveOverride(group.marginType, index)
                        }
                        showQuantityInput
                      />
                    ))}
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddOverride(group.marginType)}
                  className="w-full"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Quantity Override
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function YieldSection({
  defaultYield,
  overrides,
  allYields,
  onYieldsChange,
}: {
  defaultYield: ItemYield | undefined;
  overrides: ItemYield[];
  allYields: ItemYield[];
  onYieldsChange: (yields: ItemYield[]) => void;
}) {
  const handleAddDefault = () => {
    const newYield: ItemYield = { percentage: "100" };
    onYieldsChange([newYield, ...allYields]);
  };

  const handleDefaultChange = (value: string) => {
    if (!defaultYield) {
      return;
    }
    onYieldsChange([{ ...defaultYield, percentage: value }, ...overrides]);
  };

  const handleOverrideChange = (
    index: number,
    field: "minQty" | "percentage",
    value: string | number,
  ) => {
    const updatedOverrides = [...overrides];
    if (field === "minQty") {
      const numValue =
        typeof value === "number" ? value : parseInt(value.toString(), 10);
      updatedOverrides[index] = {
        ...updatedOverrides[index],
        minQty: isNaN(numValue) ? 1 : numValue,
      };
    } else {
      updatedOverrides[index] = {
        ...updatedOverrides[index],
        percentage: value.toString(),
      };
    }
    onYieldsChange(
      defaultYield ? [defaultYield, ...updatedOverrides] : updatedOverrides,
    );
  };

  const handleRemoveOverride = (index: number) => {
    const updatedOverrides = overrides.filter((_, i) => i !== index);
    onYieldsChange(
      defaultYield ? [defaultYield, ...updatedOverrides] : updatedOverrides,
    );
  };

  const handleAddOverride = () => {
    const maxQty = Math.max(
      0,
      ...overrides
        .map((y) => y.minQty)
        .filter((q): q is number => q !== undefined),
    );
    const newOverride: ItemYield = {
      minQty: maxQty + 10,
      percentage: "100",
    };
    onYieldsChange(
      defaultYield
        ? [defaultYield, ...overrides, newOverride]
        : [...overrides, newOverride],
    );
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-semibold">Default Yield Rate</Label>
      <p className="text-xs text-muted-foreground">
        Applied to all new items created by the agent when no yield rate is
        specified. 100% means no scrap.
      </p>

      {!defaultYield ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddDefault}
          className="w-full"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Default Yield Rate
        </Button>
      ) : (
        <div className="space-y-3">
          <div className="bg-gray-50 rounded-md p-3 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-gray-600">
                Default (All Quantities)
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onYieldsChange(overrides)}
              >
                <span className="text-xs">Remove</span>
              </Button>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Percentage (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={defaultYield.percentage}
                onChange={(e) => handleDefaultChange(e.target.value)}
                placeholder="100"
                className="h-8"
              />
            </div>
          </div>

          {overrides.length > 0 && (
            <div className="space-y-2 pl-4 border-l-2 border-gray-200">
              <span className="text-xs font-medium text-gray-600">
                Quantity Overrides
              </span>
              {overrides.map((override, index) => (
                <div
                  key={index}
                  className="bg-blue-50 rounded-md p-3 space-y-3"
                >
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-blue-900">
                      At Quantity {override.minQty || 1}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveOverride(index)}
                    >
                      <span className="text-xs">Remove</span>
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={override.minQty?.toString() ?? ""}
                        onChange={(e) => {
                          const numVal = parseInt(e.target.value, 10);
                          if (!isNaN(numVal)) {
                            handleOverrideChange(index, "minQty", numVal);
                          }
                        }}
                        placeholder="1"
                        className="h-8"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Percentage (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={override.percentage}
                        onChange={(e) =>
                          handleOverrideChange(
                            index,
                            "percentage",
                            e.target.value,
                          )
                        }
                        placeholder="100"
                        className="h-8"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddOverride}
            className="w-full"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Quantity Override
          </Button>
        </div>
      )}
    </div>
  );
}
